<template>
    <div class="fm-grid">
        <div class="d-flex align-content-start flex-wrap">
            <div v-if="!isRootPath" v-on:click="levelUp" class="fm-grid-item text-center border rounded-2 border-2">
                <div class="fm-item-icon">
                    <i class="bi bi-arrow-90deg-up pb-2" />
                </div>
                <div class="fm-item-info"><strong>..</strong></div>
            </div>

            <div
                class="fm-grid-item text-center unselectable border rounded-2 border-2"
                v-for="(directory, index) in directories"
                v-bind:key="`d-${index}`"
                v-bind:title="directory.basename"
                v-bind:class="{ active: checkSelect('directories', directory.path) }"
                v-on:click="selectItem('directories', directory.path, $event)"
                v-on:dblclick.stop="selectDirectory(directory.path)"
                v-on:contextmenu.prevent="contextMenu(directory, $event)"
            >
                <div class="fm-item-icon">

                  <svg v-if="acl && directory.acl === 0" xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="currentColor" class="bi bi-lock-fill icon_color_yellow" viewBox="0 0 16 16">
                    <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2m3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2"/>
                  </svg>

                  <svg v-else xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="currentColor" class="bi bi-folder-fill icon_color_yellow" viewBox="0 0 16 16">
                    <path d="M9.828 3h3.982a2 2 0 0 1 1.992 2.181l-.637 7A2 2 0 0 1 13.174 14H2.825a2 2 0 0 1-1.991-1.819l-.637-7a2 2 0 0 1 .342-1.31L.5 3a2 2 0 0 1 2-2h3.672a2 2 0 0 1 1.414.586l.828.828A2 2 0 0 0 9.828 3m-8.322.12q.322-.119.684-.12h5.396l-.707-.707A1 1 0 0 0 6.172 2H2.5a1 1 0 0 0-1 .981z"/>
                  </svg>
                </div>
                <div class="fm-item-info">{{ directory.basename }}</div>
            </div>

            <div
                class="fm-grid-item text-center unselectable border rounded-2 border-2"
                v-for="(file, index) in files"
                v-bind:key="`f-${index}`"
                v-bind:title="file.basename"
                v-bind:class="{ active: checkSelect('files', file.path) }"
                v-on:click="selectItem('files', file.path, $event)"
                v-on:dblclick="selectAction(file.path, file.extension)"
                v-on:contextmenu.prevent="contextMenu(file, $event)"
            >
                <div class="fm-item-icon">
                  <svg v-if="acl && file.acl === 0" xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="currentColor" class="bi bi-file-earmark-lock2 icon_color_yellow" viewBox="0 0 16 16">
                    <path d="M10 7v1.076c.54.166 1 .597 1 1.224v2.4c0 .816-.781 1.3-1.5 1.3h-3c-.719 0-1.5-.484-1.5-1.3V9.3c0-.627.46-1.058 1-1.224V7a2 2 0 1 1 4 0M7 7v1h2V7a1 1 0 0 0-2 0"/>
                    <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"/>
                  </svg>
                  <thumbnail v-else-if="thisImage(file.extension)" v-bind:disk="disk" v-bind:file="file"></thumbnail>
                  <i v-else class="bi pb-2 fs-3qx icon_color_yellow" v-bind:class="extensionToIcon(file.extension)" />
                </div>
                <div class="fm-item-info">
                    {{ `${file.filename}.${file.extension}` }}
                    <br />
                    {{ bytesToHuman(file.size) }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import translate from '@/Modules/FileManager/mixins/translate';
import helper from '@/Modules/FileManager/mixins/helper';
import managerHelper from '@/Modules/FileManager/views/components/manager/mixins/manager';
import Thumbnail from '@/Modules/FileManager/views/components/manager/Thumbnail.vue';

export default {
    name: 'GridView',
    components: { Thumbnail },
    mixins: [translate, helper, managerHelper],
    data() {
        return {
            disk: '',
        };
    },
    props: {
        manager: { type: String, required: true },
    },
    mounted() {
        this.disk = this.selectedDisk;
    },
    beforeUpdate() {
        // if disk changed
        if (this.disk !== this.selectedDisk) {
            this.disk = this.selectedDisk;
        }
    },
    computed: {
        /**
         * Image extensions list
         * @returns {*}
         */
        imageExtensions() {
            return this.$store.state.fm.settings.imageExtensions;
        },
    },
    methods: {
        /**
         * Check file extension (image or no)
         * @param extension
         * @returns {boolean}
         */
        thisImage(extension) {
            // extension not found
            if (!extension) return false;

            return this.imageExtensions.includes(extension.toLowerCase());
        },
    },
};
</script>

<style lang="scss">

.icon_color_yellow {
  color: #ffd542 !important;
}

.fm-grid {
    padding-top: 1rem;

    .fm-grid-item {
        position: relative;
        width: 125px;
        padding: 0.4rem;
        margin-bottom: 1rem;
        margin-right: 1rem;
        border-radius: 5px;

        &.active {
            background-color: #cff4fc;
            box-shadow: 3px 2px 5px gray;
        }

        &:not(.active):hover {
            background-color: #f8f9fa;
            box-shadow: 3px 2px 5px gray;
        }

        .fm-item-icon {
            font-size: 5rem;
            cursor: pointer;
        }

        .fm-item-icon > i,
        .fm-item-icon > figure > i {
            color: #6c757d;
        }

        .fm-item-info {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }
}
</style>
